import {
  isRideService,
  priceFromLabel,
  rankPartner,
  ratingLabel,
  viatorDurationLabel,
  viatorTitleLabel,
  withViatorTracking,
} from "@/lib/viator";

/**
 * The bookable experiences the site's banner spaces promote.
 *
 * A banner is one affiliate click: a photo, the experience, its price and a
 * booking link. Viator leads (the guide already caches its catalog), FareHarbor
 * fills in from the local operators who book through it, and GetYourGuide runs
 * alongside them — paste the partner id below once and every GetYourGuide
 * banner starts carrying the tracking automatically.
 */

/** Paste your GetYourGuide partner id to attach tracking to those banners. */
export const GETYOURGUIDE_PARTNER_ID = "";

const GETYOURGUIDE_BASE = "https://www.getyourguide.com/s/";

export const GETYOURGUIDE_QUERIES = [
  { q: "horseback riding Puerto Rico", en: "Horseback riding", es: "Paseo a caballo" },
  { q: "El Yunque rainforest tour", en: "El Yunque rainforest", es: "El Yunque" },
  { q: "Icacos catamaran", en: "Catamaran to Icacos", es: "Catamarán a Icacos" },
  { q: "bioluminescent bay kayak", en: "Bioluminescent bay", es: "Bahía bioluminiscente" },
  { q: "snorkeling San Juan", en: "Snorkeling in San Juan", es: "Snorkel en San Juan" },
  { q: "Old San Juan food tour", en: "Old San Juan food tour", es: "Recorrido gastronómico" },
];

const getYourGuideUrl = (query) => {
  const url = new URL(GETYOURGUIDE_BASE);
  url.searchParams.set("q", query);
  url.searchParams.set("searchSource", "1");
  if (GETYOURGUIDE_PARTNER_ID) url.searchParams.set("partner_id", GETYOURGUIDE_PARTNER_ID);
  return url.toString();
};

const withHttps = (url) => (/^https?:\/\//i.test(url || "") ? url : `https://${url || ""}`);

const imageOf = (item) =>
  item?.cover_image || item?.storefront_image || (item?.images || []).find(Boolean) || "";

/** The photographs a banner slides through, first shot first, without repeats. */
const photosOf = (item) =>
  Array.from(
    new Set(
      [
        item?.cover_image,
        item?.storefront_image,
        ...(item?.images || []),
        ...(item?.gallery_images || []),
      ].filter(Boolean)
    )
  ).slice(0, 5);

/** The few lines a banner shows, built from what a partner record carries. */
const metaLine = (bits) => bits.filter(Boolean).join(" · ");

/** Every experience a banner can send a visitor to, in the order they run. */
export function experienceSpots({ viator = [], listings = [], lang = "en" }) {
  const es = lang === "es";

  const viatorSpots = rankPartner(
    (viator || []).filter(
      (item) => item && item.booking_url && !isRideService(item) && imageOf(item)
    )
  )
    .slice(0, 30)
    .map((item) => ({
      key: `viator-${item.id}`,
      title: viatorTitleLabel(item, lang),
      brand: "viator",
      overline: es ? "Tours y experiencias" : "Tours & experiences",
      blurb: (es ? item.short_description_es : item.short_description) || item.short_description || "",
      meta: metaLine(
        [
          priceFromLabel(item.price_from, lang),
          item.rating ? `★ ${ratingLabel(item.rating)}${item.review_count ? ` (${Number(item.review_count).toLocaleString()})` : ""}` : "",
          viatorDurationLabel(item.duration_text, lang),
        ]
      ),
      thumb: imageOf(item),
      thumbs: photosOf(item),
      // Every banner click carries the guide's Viator affiliate id.
      href: withViatorTracking(withHttps(item.booking_url)),
      cta: es ? "Reservar" : "Book",
    }));

  const fareHarborSpots = (listings || [])
    .filter(
      (item) =>
        item &&
        item.name &&
        item.affiliate_provider === "FareHarbor" &&
        (item.primary_action_url || item.booking_url)
    )
    .map((item) => ({
      key: `fareharbor-${item.id}`,
      title: item.name,
      brand: "fareharbor",
      overline: "FareHarbor",
      blurb: (es ? item.short_description_es : item.short_description) || item.short_description || "",
      meta: metaLine([item.price_label, item.area]),
      thumb: imageOf(item),
      thumbs: photosOf(item),
      href: withHttps(item.primary_action_url || item.booking_url),
      cta: es ? "Reservar" : "Book",
    }));

  const getYourGuideSpots = GETYOURGUIDE_QUERIES.map((query) => ({
    key: `getyourguide-${query.q}`,
    title: es ? query.es : query.en,
    brand: "getyourguide",
    overline: "GetYourGuide",
    meta: es ? "Tours y entradas" : "Tours & tickets",
    thumb: "",
    href: getYourGuideUrl(query.q),
    cta: es ? "Buscar" : "Browse",
  }));

  // Viator leads; one of the other partners follows every third experience, so
  // a run of banners shows all three without any one of them taking over.
  const others = [...fareHarborSpots, ...getYourGuideSpots];
  if (!others.length) return viatorSpots;

  const out = [];
  let next = 0;
  viatorSpots.forEach((spot, i) => {
    out.push(spot);
    if ((i + 1) % 3 === 0 && next < others.length) out.push(others[next++]);
  });
  while (next < others.length) out.push(others[next++]);
  return out;
}